import React, { useEffect, useState } from "react";
import {
    Loader2,
    Save,
    Sparkles,
    Layers,
    Package,
    Boxes,
    Handshake,
    Award,
    PanelBottom,
    Plus,
    Trash2,
    Image as ImageIcon,
    Quote,
    HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import ImageInput from "../components/admin/ImageInput";

const SUB_TABS = [
    { k: "hero", label: "Hero", icon: Sparkles },
    { k: "differentiators", label: "Diferenciadores", icon: Award },
    { k: "lines", label: "Linhas", icon: Layers },
    { k: "line_details", label: "Páginas Linhas", icon: Layers },
    { k: "partners", label: "Parceiros", icon: Handshake },
    { k: "testimonials", label: "Testemunhos", icon: Quote },
    { k: "faq", label: "FAQ", icon: HelpCircle },
    { k: "footer", label: "Footer", icon: PanelBottom },
];

export default function AdminContent({ apiAuth }) {
    const [sub, setSub] = useState("hero");
    return (
        <div className="space-y-6" data-testid="admin-content-panel">
            <nav className="flex gap-2 flex-wrap" aria-label="Content sections">
                {SUB_TABS.map((t) => {
                    const Icon = t.icon;
                    const active = sub === t.k;
                    return (
                        <button
                            key={t.k}
                            type="button"
                            onClick={() => setSub(t.k)}
                            data-testid={`admin-content-sub-${t.k}`}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all ${
                                active
                                    ? "bg-brand-ink text-white shadow-pop"
                                    : "bg-white text-brand-ink hover:bg-brand-cream"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </nav>
            <div>
                {sub === "hero" && <HeroEditor apiAuth={apiAuth} />}
                {sub === "differentiators" && <DifferentiatorsEditor apiAuth={apiAuth} />}
                {sub === "lines" && <LinesEditor apiAuth={apiAuth} />}
                {sub === "line_details" && <LineDetailsEditor apiAuth={apiAuth} />}
                {sub === "partners" && <PartnersEditor apiAuth={apiAuth} />}
                {sub === "testimonials" && <TestimonialsEditor apiAuth={apiAuth} />}
                {sub === "faq" && <FaqEditor apiAuth={apiAuth} />}
                {sub === "footer" && <FooterEditor apiAuth={apiAuth} />}
            </div>
        </div>
    );
}

// ============================================================
// Generic helpers
// ============================================================
const Card = ({ title, children, action }) => (
    <section className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-6 md:p-8 space-y-4">
        {title && (
            <header className="flex items-center justify-between gap-4">
                <h3 className="font-display font-medium text-lg text-brand-ink">{title}</h3>
                {action}
            </header>
        )}
        {children}
    </section>
);

const Field = ({ label, children, hint }) => (
    <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft mb-1.5 block">
            {label}
        </label>
        {children}
        {hint && <p className="mt-1 text-[11px] text-brand-ink-soft">{hint}</p>}
    </div>
);

const TxtInput = (props) => (
    <input
        type="text"
        {...props}
        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
    />
);

const NumInput = (props) => (
    <input
        type="number"
        step="0.01"
        {...props}
        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
    />
);

const TxtArea = (props) => (
    <textarea
        rows={3}
        {...props}
        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-green resize-none"
    />
);

const SaveButton = ({ onClick, busy, testid = "content-save", label = "Guardar alterações" }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid={testid}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-5 py-2.5 text-xs font-semibold transition-all shadow-pop disabled:opacity-60"
    >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        {label}
    </button>
);

const StringList = ({ items, onChange, placeholder, testid }) => {
    const update = (idx, val) =>
        onChange(items.map((it, i) => (i === idx ? val : it)));
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
    const add = () => onChange([...(items || []), ""]);
    return (
        <div className="space-y-2" data-testid={testid}>
            {(items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                    <TxtInput
                        value={it}
                        onChange={(e) => update(i, e.target.value)}
                        placeholder={placeholder}
                        data-testid={`${testid}-item-${i}`}
                    />
                    <button
                        type="button"
                        onClick={() => remove(i)}
                        className="h-9 w-9 flex-shrink-0 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                        data-testid={`${testid}-remove-${i}`}
                    >
                        <Trash2 className="h-3.5 w-3.5 mx-auto" />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                data-testid={`${testid}-add`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold"
            >
                <Plus className="h-3 w-3" /> Adicionar
            </button>
        </div>
    );
};

// ============================================================
// HERO EDITOR
// ============================================================
function HeroEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/hero")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar hero"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const u = (k) => (e) =>
        setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

    const save = async () => {
        setBusy(true);
        try {
            await apiAuth("/admin/content/hero", {
                method: "PUT",
                data: { data },
            });
            toast.success("Hero atualizado");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card title="Hero da Home" action={<SaveButton onClick={save} busy={busy} testid="content-hero-save" />}>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="Badge (etiqueta superior)">
                    <TxtInput value={data.badge || ""} onChange={u("badge")} data-testid="hero-badge" />
                </Field>
                <Field label="Texto chip (canto)">
                    <TxtInput value={data.chip_text || ""} onChange={u("chip_text")} data-testid="hero-chip" />
                </Field>
                <Field label="Título — pré">
                    <TxtInput value={data.title_pre || ""} onChange={u("title_pre")} data-testid="hero-title-pre" />
                </Field>
                <Field label="Título — destacado">
                    <TxtInput value={data.title_highlight || ""} onChange={u("title_highlight")} data-testid="hero-title-highlight" />
                </Field>
                <Field label="Título — sufixo">
                    <TxtInput value={data.title_suffix || ""} onChange={u("title_suffix")} data-testid="hero-title-suffix" />
                </Field>
                <div className="md:col-span-2">
                    <Field label="Descrição">
                        <TxtArea value={data.description || ""} onChange={u("description")} data-testid="hero-description" />
                    </Field>
                </div>
                <Field label="CTA principal — texto">
                    <TxtInput value={data.cta_primary_label || ""} onChange={u("cta_primary_label")} data-testid="hero-cta1-label" />
                </Field>
                <Field label="CTA principal — link">
                    <TxtInput value={data.cta_primary_href || ""} onChange={u("cta_primary_href")} data-testid="hero-cta1-href" />
                </Field>
                <Field label="CTA secundário — texto">
                    <TxtInput value={data.cta_secondary_label || ""} onChange={u("cta_secondary_label")} data-testid="hero-cta2-label" />
                </Field>
                <Field label="CTA secundário — link">
                    <TxtInput value={data.cta_secondary_href || ""} onChange={u("cta_secondary_href")} data-testid="hero-cta2-href" />
                </Field>
                <div className="md:col-span-2">
                    <ImageInput value={data.hero_image} onChange={(v) => u("hero_image")(v)} label="Imagem hero" testid="hero-image" />
                </div>
                <Field label="Vídeo hero (URL opcional)">
                    <TxtInput value={data.hero_video || ""} onChange={u("hero_video")} data-testid="hero-video" />
                </Field>
            </div>
            <div className="grid md:grid-cols-3 gap-3 pt-4 border-t border-brand-green/10">
                <Field label="Stat 1 — número"><TxtInput value={data.stat_top_n || ""} onChange={u("stat_top_n")} data-testid="hero-stat1n" /></Field>
                <Field label="Stat 1 — etiqueta"><TxtInput value={data.stat_top_l || ""} onChange={u("stat_top_l")} data-testid="hero-stat1l" /></Field>
                <div />
                <Field label="Stat 2 — número"><TxtInput value={data.stat_mid_n || ""} onChange={u("stat_mid_n")} data-testid="hero-stat2n" /></Field>
                <Field label="Stat 2 — etiqueta"><TxtInput value={data.stat_mid_l || ""} onChange={u("stat_mid_l")} data-testid="hero-stat2l" /></Field>
                <div />
                <Field label="Stat 3 — número"><TxtInput value={data.stat_bot_n || ""} onChange={u("stat_bot_n")} data-testid="hero-stat3n" /></Field>
                <Field label="Stat 3 — etiqueta"><TxtInput value={data.stat_bot_l || ""} onChange={u("stat_bot_l")} data-testid="hero-stat3l" /></Field>
            </div>
        </Card>
    );
}

// ============================================================
// DIFFERENTIATORS EDITOR
// ============================================================
const DIFF_ICON_OPTIONS = [
    "Scale", "Snowflake", "Recycle", "Rocket", "ShieldCheck", "Leaf",
    "Zap", "Heart", "Award", "Clock", "TrendingUp", "Package",
    "Star", "Truck", "Sparkles", "Target", "CheckCircle", "Droplets",
];

const DIFF_VARIANT_OPTIONS = [
    { v: "light", label: "Clara (branca)" },
    { v: "orange", label: "Laranja" },
    { v: "green-soft", label: "Verde suave" },
    { v: "dark", label: "Escura" },
];

const DIFF_SLOT_LABELS = ["Slot 1 — grande", "Slot 2 — pequeno", "Slot 3 — pequeno", "Slot 4 — médio"];

function DifferentiatorsEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/differentiators")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar diferenciadores"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const u = (k) => (e) =>
        setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

    const updateItem = (idx, field, value) =>
        setData((d) => ({
            ...d,
            items: (d.items || []).map((it, i) =>
                i === idx ? { ...it, [field]: value } : it,
            ),
        }));

    const save = async () => {
        const items = data.items || [];
        if (items.length !== 4) {
            toast.error("Tem de haver exactamente 4 diferenciadores");
            return;
        }
        for (let i = 0; i < 4; i++) {
            if (!items[i]?.title?.trim() || !items[i]?.text?.trim() || !items[i]?.icon_key?.trim()) {
                toast.error(`Slot ${i + 1}: preenche ícone, título e descrição`);
                return;
            }
        }
        setBusy(true);
        try {
            await apiAuth("/admin/content/differentiators", {
                method: "PUT",
                data: { data },
            });
            toast.success("Diferenciadores atualizados");
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string"
                ? detail
                : detail?.message || "Erro ao guardar";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card
            title="Diferenciadores da Home"
            action={<SaveButton onClick={save} busy={busy} testid="content-diff-save" />}
        >
            <div className="grid md:grid-cols-3 gap-4">
                <Field label="Etiqueta (eyebrow)">
                    <TxtInput
                        value={data.eyebrow || ""}
                        onChange={u("eyebrow")}
                        data-testid="diff-eyebrow"
                    />
                </Field>
                <Field label="Título — pré">
                    <TxtInput
                        value={data.title_pre || ""}
                        onChange={u("title_pre")}
                        data-testid="diff-title-pre"
                    />
                </Field>
                <Field label="Título — destacado">
                    <TxtInput
                        value={data.title_highlight || ""}
                        onChange={u("title_highlight")}
                        data-testid="diff-title-highlight"
                    />
                </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-brand-green/10">
                {(data.items || []).slice(0, 4).map((it, idx) => (
                    <div
                        key={idx}
                        data-testid={`diff-slot-${idx}`}
                        className="rounded-2xl border-2 border-brand-green/15 p-4 space-y-3 bg-brand-green-soft/30"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-[0.15em] text-brand-ink-soft">
                                {DIFF_SLOT_LABELS[idx]}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Ícone">
                                <select
                                    value={it.icon_key || ""}
                                    onChange={(e) => updateItem(idx, "icon_key", e.target.value)}
                                    data-testid={`diff-icon-${idx}`}
                                    className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                                >
                                    {DIFF_ICON_OPTIONS.map((k) => (
                                        <option key={k} value={k}>{k}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Variante visual">
                                <select
                                    value={it.variant || "light"}
                                    onChange={(e) => updateItem(idx, "variant", e.target.value)}
                                    data-testid={`diff-variant-${idx}`}
                                    className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                                >
                                    {DIFF_VARIANT_OPTIONS.map((o) => (
                                        <option key={o.v} value={o.v}>{o.label}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        <Field label="Título">
                            <TxtInput
                                value={it.title || ""}
                                onChange={(e) => updateItem(idx, "title", e.target.value)}
                                data-testid={`diff-title-${idx}`}
                            />
                        </Field>
                        <Field label="Descrição">
                            <TxtArea
                                value={it.text || ""}
                                onChange={(e) => updateItem(idx, "text", e.target.value)}
                                data-testid={`diff-text-${idx}`}
                                rows={3}
                            />
                        </Field>
                    </div>
                ))}
            </div>
        </Card>
    );
}

// ============================================================
// PRODUCTS EDITOR
// ============================================================
function ProductsEditor({ apiAuth }) {
    const [items, setItems] = useState(null);
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        apiAuth("/admin/catalog/products")
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("Erro a carregar produtos"));
    }, [apiAuth]);

    if (!items) return <Loading />;

    const update = (idx, key, val) =>
        setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));

    const save = async (idx) => {
        const it = items[idx];
        setSavingId(it.id);
        try {
            const payload = {
                name: it.name,
                description: it.description,
                price: parseFloat(it.price) || 0,
                color: it.color,
                image: it.image,
            };
            await apiAuth(`/admin/catalog/products/${it.id}`, {
                method: "PUT",
                data: payload,
            });
            toast.success(`${it.name} atualizado`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="space-y-4">
            {items.map((it, i) => (
                <Card
                    key={it.id}
                    title={`${it.name} (${it.id})`}
                    action={
                        <SaveButton
                            onClick={() => save(i)}
                            busy={savingId === it.id}
                            testid={`product-save-${it.id}`}
                            label="Guardar"
                        />
                    }
                >
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
                            <Field label="Nome">
                                <TxtInput
                                    value={it.name || ""}
                                    onChange={(e) => update(i, "name", e.target.value)}
                                    data-testid={`product-name-${it.id}`}
                                />
                            </Field>
                            <Field label="Preço (€)">
                                <NumInput
                                    value={it.price ?? ""}
                                    onChange={(e) => update(i, "price", e.target.value)}
                                    data-testid={`product-price-${it.id}`}
                                />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label="Descrição">
                                    <TxtArea
                                        value={it.description || ""}
                                        onChange={(e) => update(i, "description", e.target.value)}
                                        data-testid={`product-desc-${it.id}`}
                                    />
                                </Field>
                            </div>
                            <Field label="Cor (hex)" hint="Ex: #B95C50">
                                <TxtInput
                                    value={it.color || ""}
                                    onChange={(e) => update(i, "color", e.target.value)}
                                    data-testid={`product-color-${it.id}`}
                                />
                            </Field>
                        </div>
                        <div>
                            <ImageInput
                                value={it.image}
                                onChange={(v) => update(i, "image", v)}
                                label="Imagem do produto"
                                testid={`product-image-${it.id}`}
                            />
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );
}

// ============================================================
// KITS EDITOR
// ============================================================
function KitsEditor({ apiAuth }) {
    const [items, setItems] = useState(null);
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        apiAuth("/admin/catalog/kits")
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("Erro a carregar kits"));
    }, [apiAuth]);

    if (!items) return <Loading />;

    const update = (idx, key, val) =>
        setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));

    const save = async (idx) => {
        const it = items[idx];
        setSavingId(it.id);
        try {
            await apiAuth(`/admin/catalog/kits/${it.id}`, {
                method: "PUT",
                data: {
                    name: it.name,
                    tagline: it.tagline,
                    description: it.description,
                    price: parseFloat(it.price) || 0,
                    color: it.color,
                    image: it.image,
                    includes: it.includes,
                    yield_cups: parseInt(it.yield_cups || 0, 10),
                    badge: it.badge,
                },
            });
            toast.success(`${it.name} atualizado`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="space-y-4">
            {items.map((it, i) => (
                <Card
                    key={it.id}
                    title={`${it.name} (${it.id})`}
                    action={
                        <SaveButton
                            onClick={() => save(i)}
                            busy={savingId === it.id}
                            testid={`kit-save-${it.id}`}
                            label="Guardar"
                        />
                    }
                >
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
                            <Field label="Nome">
                                <TxtInput value={it.name || ""} onChange={(e) => update(i, "name", e.target.value)} data-testid={`kit-name-${it.id}`} />
                            </Field>
                            <Field label="Preço (€)">
                                <NumInput value={it.price ?? ""} onChange={(e) => update(i, "price", e.target.value)} data-testid={`kit-price-${it.id}`} />
                            </Field>
                            <Field label="Tagline">
                                <TxtInput value={it.tagline || ""} onChange={(e) => update(i, "tagline", e.target.value)} data-testid={`kit-tagline-${it.id}`} />
                            </Field>
                            <Field label="Badge">
                                <TxtInput value={it.badge || ""} onChange={(e) => update(i, "badge", e.target.value)} data-testid={`kit-badge-${it.id}`} />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label="Descrição">
                                    <TxtArea value={it.description || ""} onChange={(e) => update(i, "description", e.target.value)} data-testid={`kit-desc-${it.id}`} />
                                </Field>
                            </div>
                            <Field label="Rendimento (copos)">
                                <NumInput value={it.yield_cups ?? ""} onChange={(e) => update(i, "yield_cups", e.target.value)} data-testid={`kit-yield-${it.id}`} />
                            </Field>
                            <Field label="Cor (hex)">
                                <TxtInput value={it.color || ""} onChange={(e) => update(i, "color", e.target.value)} data-testid={`kit-color-${it.id}`} />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label="Inclui (uma linha por item)">
                                    <StringList
                                        items={it.includes || []}
                                        onChange={(arr) => update(i, "includes", arr)}
                                        placeholder="Ex: 6 doses Morango"
                                        testid={`kit-includes-${it.id}`}
                                    />
                                </Field>
                            </div>
                        </div>
                        <div>
                            <ImageInput
                                value={it.image}
                                onChange={(v) => update(i, "image", v)}
                                label="Imagem do kit"
                                testid={`kit-image-${it.id}`}
                            />
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );
}

// ============================================================
// LINES EDITOR
// ============================================================
function LinesEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/lines")
            .then((r) => setData(r.data || { items: [] }))
            .catch(() => toast.error("Erro a carregar linhas"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const updateItem = (idx, key, val) =>
        setData((d) => ({
            ...d,
            items: d.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)),
        }));

    const save = async () => {
        setBusy(true);
        try {
            await apiAuth("/admin/content/lines", { method: "PUT", data: { data } });
            toast.success("Linhas atualizadas");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <SaveButton onClick={save} busy={busy} testid="lines-save" />
            </div>
            {(data.items || []).map((it, i) => (
                <Card key={it.id || i} title={`Linha ${it.name || it.id}`}>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
                            <Field label="Nome (ex: FRUIT)">
                                <TxtInput value={it.name || ""} onChange={(e) => updateItem(i, "name", e.target.value)} data-testid={`line-name-${it.id}`} />
                            </Field>
                            <Field label="Subtítulo">
                                <TxtInput value={it.subtitle || ""} onChange={(e) => updateItem(i, "subtitle", e.target.value)} data-testid={`line-subtitle-${it.id}`} />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label="Descrição">
                                    <TxtArea value={it.description || ""} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`line-desc-${it.id}`} />
                                </Field>
                            </div>
                            <Field label="Cor (hex)">
                                <TxtInput value={it.color || ""} onChange={(e) => updateItem(i, "color", e.target.value)} data-testid={`line-color-${it.id}`} />
                            </Field>
                            <div />
                            <div className="md:col-span-2">
                                <Field label="Características (uma por linha)">
                                    <StringList
                                        items={it.features || []}
                                        onChange={(arr) => updateItem(i, "features", arr)}
                                        placeholder="Ex: Rico em proteína"
                                        testid={`line-features-${it.id}`}
                                    />
                                </Field>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <ImageInput value={it.image} onChange={(v) => updateItem(i, "image", v)} label="Imagem grande" testid={`line-image-${it.id}`} />
                            <ImageInput value={it.mark} onChange={(v) => updateItem(i, "mark", v)} label="Logo/marca da linha" testid={`line-mark-${it.id}`} />
                        </div>
                    </div>
                </Card>
            ))}
            <div className="flex justify-end">
                <SaveButton onClick={save} busy={busy} testid="lines-save-bottom" />
            </div>
        </div>
    );
}

// ============================================================
// PARTNERS EDITOR
// ============================================================
const ICON_OPTIONS = [
    "TrendingUp", "MapPin", "GraduationCap", "Megaphone", "Package",
    "ShieldCheck", "Truck", "Handshake", "Shirt", "ShoppingBag",
    "Coffee", "Store", "Gift", "Palette", "Star", "Sparkles",
];

function PartnersEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/partners")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar parceiros"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const u = (k) => (e) =>
        setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

    const arrUpd = (key, idx, field, val) =>
        setData((d) => ({
            ...d,
            [key]: (d[key] || []).map((it, i) => (i === idx ? { ...it, [field]: val } : it)),
        }));
    const arrRm = (key, idx) =>
        setData((d) => ({ ...d, [key]: (d[key] || []).filter((_, i) => i !== idx) }));
    const arrAdd = (key, factory) =>
        setData((d) => ({ ...d, [key]: [...(d[key] || []), factory()] }));

    const save = async () => {
        setBusy(true);
        try {
            await apiAuth("/admin/content/partners", { method: "PUT", data: { data } });
            toast.success("Página de parceiros atualizada");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    };

    const IconSelect = ({ value, onChange, testid }) => (
        <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-3 py-2 text-xs text-brand-ink focus:outline-none focus:border-brand-green"
        >
            <option value="">— ícone —</option>
            {ICON_OPTIONS.map((i) => (
                <option key={i} value={i}>{i}</option>
            ))}
        </select>
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <SaveButton onClick={save} busy={busy} testid="partners-save" />
            </div>

            <Card title="Hero da página">
                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Badge"><TxtInput value={data.hero_badge || ""} onChange={u("hero_badge")} data-testid="partners-hero-badge" /></Field>
                    <Field label="Título — pré"><TxtInput value={data.hero_title_pre || ""} onChange={u("hero_title_pre")} data-testid="partners-hero-pre" /></Field>
                    <Field label="Título — destacado"><TxtInput value={data.hero_title_highlight || ""} onChange={u("hero_title_highlight")} data-testid="partners-hero-highlight" /></Field>
                    <Field label="Título — sufixo"><TxtInput value={data.hero_title_suffix || ""} onChange={u("hero_title_suffix")} data-testid="partners-hero-suffix" /></Field>
                    <div className="md:col-span-2">
                        <Field label="Descrição"><TxtArea value={data.hero_description || ""} onChange={u("hero_description")} data-testid="partners-hero-desc" /></Field>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3 pt-4 border-t border-brand-green/10">
                    <ImageInput value={data.hero_image} onChange={(v) => u("hero_image")(v)} label="Imagem hero" testid="partners-hero-image" />
                    <ImageInput value={data.merch_image} onChange={(v) => u("merch_image")(v)} label="Imagem merchandising" testid="partners-merch-image" />
                    <ImageInput value={data.counter_image} onChange={(v) => u("counter_image")(v)} label="Imagem balcão" testid="partners-counter-image" />
                </div>
                <div className="grid md:grid-cols-3 gap-3 pt-4 border-t border-brand-green/10">
                    {[1, 2, 3].map((n) => (
                        <div key={n} className="grid grid-cols-2 gap-2">
                            <Field label={`Stat ${n} nº`}><TxtInput value={data[`stat_${n}_n`] || ""} onChange={u(`stat_${n}_n`)} data-testid={`partners-stat${n}n`} /></Field>
                            <Field label={`Stat ${n} txt`}><TxtInput value={data[`stat_${n}_l`] || ""} onChange={u(`stat_${n}_l`)} data-testid={`partners-stat${n}l`} /></Field>
                        </div>
                    ))}
                </div>
                <div className="grid md:grid-cols-3 gap-3 pt-4 border-t border-brand-green/10">
                    <Field label="Email contacto"><TxtInput value={data.contact_email || ""} onChange={u("contact_email")} data-testid="partners-email" /></Field>
                    <Field label="Telefone contacto"><TxtInput value={data.contact_phone || ""} onChange={u("contact_phone")} data-testid="partners-phone" /></Field>
                    <Field label="WhatsApp (só dígitos)" hint="Ex: 351912345678"><TxtInput value={data.contact_whatsapp || ""} onChange={u("contact_whatsapp")} data-testid="partners-whatsapp" /></Field>
                </div>
            </Card>

            <Card
                title="Benefícios"
                action={
                    <button type="button" onClick={() => arrAdd("benefits", () => ({ icon_key: "Star", title: "Novo", text: "" }))} className="inline-flex items-center gap-1 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold" data-testid="partners-add-benefit">
                        <Plus className="h-3 w-3" /> Adicionar
                    </button>
                }
            >
                <div className="space-y-3">
                    {(data.benefits || []).map((b, i) => (
                        <div key={i} className="rounded-2xl bg-brand-cream/40 ring-1 ring-brand-green/10 p-3 grid md:grid-cols-12 gap-2 items-end">
                            <div className="md:col-span-2"><Field label="Ícone"><IconSelect value={b.icon_key} onChange={(v) => arrUpd("benefits", i, "icon_key", v)} testid={`benefit-icon-${i}`} /></Field></div>
                            <div className="md:col-span-3"><Field label="Título"><TxtInput value={b.title || ""} onChange={(e) => arrUpd("benefits", i, "title", e.target.value)} data-testid={`benefit-title-${i}`} /></Field></div>
                            <div className="md:col-span-6"><Field label="Texto"><TxtInput value={b.text || ""} onChange={(e) => arrUpd("benefits", i, "text", e.target.value)} data-testid={`benefit-text-${i}`} /></Field></div>
                            <div className="md:col-span-1">
                                <button type="button" onClick={() => arrRm("benefits", i)} className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100" data-testid={`benefit-remove-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card
                title="Condições da proposta comercial"
                action={
                    <button type="button" onClick={() => arrAdd("proposal", () => ({ label: "Nova", value: "", sub: "" }))} className="inline-flex items-center gap-1 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold" data-testid="partners-add-proposal">
                        <Plus className="h-3 w-3" /> Adicionar
                    </button>
                }
            >
                <div className="space-y-3">
                    {(data.proposal || []).map((p, i) => (
                        <div key={i} className="rounded-2xl bg-brand-cream/40 ring-1 ring-brand-green/10 p-3 grid md:grid-cols-12 gap-2 items-end">
                            <div className="md:col-span-4"><Field label="Etiqueta"><TxtInput value={p.label || ""} onChange={(e) => arrUpd("proposal", i, "label", e.target.value)} data-testid={`proposal-label-${i}`} /></Field></div>
                            <div className="md:col-span-3"><Field label="Valor"><TxtInput value={p.value || ""} onChange={(e) => arrUpd("proposal", i, "value", e.target.value)} data-testid={`proposal-value-${i}`} /></Field></div>
                            <div className="md:col-span-4"><Field label="Sub-texto"><TxtInput value={p.sub || ""} onChange={(e) => arrUpd("proposal", i, "sub", e.target.value)} data-testid={`proposal-sub-${i}`} /></Field></div>
                            <div className="md:col-span-1">
                                <button type="button" onClick={() => arrRm("proposal", i)} className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100" data-testid={`proposal-remove-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card
                title="Como começar (passos)"
                action={
                    <button type="button" onClick={() => arrAdd("steps", () => ({ n: (data.steps?.length || 0) + 1, title: "Novo", text: "" }))} className="inline-flex items-center gap-1 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold" data-testid="partners-add-step">
                        <Plus className="h-3 w-3" /> Adicionar
                    </button>
                }
            >
                <div className="space-y-3">
                    {(data.steps || []).map((s, i) => (
                        <div key={i} className="rounded-2xl bg-brand-cream/40 ring-1 ring-brand-green/10 p-3 grid md:grid-cols-12 gap-2 items-end">
                            <div className="md:col-span-1"><Field label="Nº"><TxtInput value={s.n ?? ""} onChange={(e) => arrUpd("steps", i, "n", parseInt(e.target.value, 10) || 0)} data-testid={`step-n-${i}`} /></Field></div>
                            <div className="md:col-span-3"><Field label="Título"><TxtInput value={s.title || ""} onChange={(e) => arrUpd("steps", i, "title", e.target.value)} data-testid={`step-title-${i}`} /></Field></div>
                            <div className="md:col-span-7"><Field label="Texto"><TxtInput value={s.text || ""} onChange={(e) => arrUpd("steps", i, "text", e.target.value)} data-testid={`step-text-${i}`} /></Field></div>
                            <div className="md:col-span-1">
                                <button type="button" onClick={() => arrRm("steps", i)} className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100" data-testid={`step-remove-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card
                title="Merchandising"
                action={
                    <button type="button" onClick={() => arrAdd("merch_items", () => ({ icon_key: "Gift", name: "Novo item", tag: "" }))} className="inline-flex items-center gap-1 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold" data-testid="partners-add-merch">
                        <Plus className="h-3 w-3" /> Adicionar
                    </button>
                }
            >
                <div className="space-y-3">
                    {(data.merch_items || []).map((m, i) => (
                        <div key={i} className="rounded-2xl bg-brand-cream/40 ring-1 ring-brand-green/10 p-3 grid md:grid-cols-12 gap-2 items-end">
                            <div className="md:col-span-2"><Field label="Ícone"><IconSelect value={m.icon_key} onChange={(v) => arrUpd("merch_items", i, "icon_key", v)} testid={`merch-icon-${i}`} /></Field></div>
                            <div className="md:col-span-4"><Field label="Nome"><TxtInput value={m.name || ""} onChange={(e) => arrUpd("merch_items", i, "name", e.target.value)} data-testid={`merch-name-${i}`} /></Field></div>
                            <div className="md:col-span-5"><Field label="Sub/tag"><TxtInput value={m.tag || ""} onChange={(e) => arrUpd("merch_items", i, "tag", e.target.value)} data-testid={`merch-tag-${i}`} /></Field></div>
                            <div className="md:col-span-1">
                                <button type="button" onClick={() => arrRm("merch_items", i)} className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100" data-testid={`merch-remove-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card
                title="FAQs"
                action={
                    <button type="button" onClick={() => arrAdd("faqs", () => ({ q: "Nova pergunta?", a: "" }))} className="inline-flex items-center gap-1 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold" data-testid="partners-add-faq">
                        <Plus className="h-3 w-3" /> Adicionar
                    </button>
                }
            >
                <div className="space-y-3">
                    {(data.faqs || []).map((f, i) => (
                        <div key={i} className="rounded-2xl bg-brand-cream/40 ring-1 ring-brand-green/10 p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="flex-1">
                                    <Field label="Pergunta"><TxtInput value={f.q || ""} onChange={(e) => arrUpd("faqs", i, "q", e.target.value)} data-testid={`faq-q-${i}`} /></Field>
                                </div>
                                <button type="button" onClick={() => arrRm("faqs", i)} className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 mt-5" data-testid={`faq-remove-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                            <Field label="Resposta"><TxtArea value={f.a || ""} onChange={(e) => arrUpd("faqs", i, "a", e.target.value)} data-testid={`faq-a-${i}`} /></Field>
                        </div>
                    ))}
                </div>
            </Card>

            <div className="flex justify-end">
                <SaveButton onClick={save} busy={busy} testid="partners-save-bottom" />
            </div>
        </div>
    );
}

const Loading = () => (
    <div className="p-16 flex justify-center bg-white rounded-2xl shadow-soft">
        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
    </div>
);

// ============================================================
// FOOTER EDITOR
// ============================================================
function FooterEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/footer")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar footer"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const u = (k) => (e) =>
        setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

    const navLinks = data.nav_links || [];
    const updateNav = (i, field, value) =>
        setData((d) => ({
            ...d,
            nav_links: (d.nav_links || []).map((l, idx) =>
                idx === i ? { ...l, [field]: value } : l,
            ),
        }));
    const addNav = () =>
        setData((d) => ({
            ...d,
            nav_links: [...(d.nav_links || []), { label: "", href: "" }],
        }));
    const removeNav = (i) =>
        setData((d) => ({
            ...d,
            nav_links: (d.nav_links || []).filter((_, idx) => idx !== i),
        }));

    const save = async () => {
        if (!(data.email || "").trim()) {
            toast.error("Email é obrigatório");
            return;
        }
        if (!(data.tagline || "").trim()) {
            toast.error("Tagline é obrigatório");
            return;
        }
        if ((data.nav_links || []).length === 0) {
            toast.error("Tem de haver pelo menos um link de navegação");
            return;
        }
        for (let i = 0; i < (data.nav_links || []).length; i++) {
            const l = data.nav_links[i];
            if (!l.label?.trim() || !l.href?.trim()) {
                toast.error(`Link #${i + 1}: preenche label e destino`);
                return;
            }
        }
        setBusy(true);
        try {
            await apiAuth("/admin/content/footer", {
                method: "PUT",
                data: { data },
            });
            toast.success("Footer atualizado");
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string"
                ? detail
                : detail?.message || "Erro ao guardar";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6">
            <Card
                title="Conteúdo geral"
                action={<SaveButton onClick={save} busy={busy} testid="footer-save" />}
            >
                <Field label="Tagline (texto sob o logo)">
                    <TxtArea
                        value={data.tagline || ""}
                        onChange={u("tagline")}
                        data-testid="footer-tagline"
                        rows={2}
                    />
                </Field>
                <Field label="Texto à direita no rodapé (após copyright)">
                    <TxtInput
                        value={data.copyright_suffix || ""}
                        onChange={u("copyright_suffix")}
                        data-testid="footer-copy-suffix"
                    />
                </Field>
            </Card>

            <Card title="Redes sociais">
                <p className="text-xs text-brand-ink-soft -mt-2">
                    Deixa em branco para esconder o ícone correspondente.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Instagram (URL completo)">
                        <TxtInput
                            value={data.instagram_url || ""}
                            onChange={u("instagram_url")}
                            placeholder="https://instagram.com/saborliquido"
                            data-testid="footer-instagram-url"
                        />
                    </Field>
                    <Field label="Facebook (URL completo)">
                        <TxtInput
                            value={data.facebook_url || ""}
                            onChange={u("facebook_url")}
                            placeholder="https://facebook.com/saborliquido"
                            data-testid="footer-facebook-url"
                        />
                    </Field>
                    <Field label="LinkedIn (URL completo)">
                        <TxtInput
                            value={data.linkedin_url || ""}
                            onChange={u("linkedin_url")}
                            placeholder="https://linkedin.com/company/saborliquido"
                            data-testid="footer-linkedin-url"
                        />
                    </Field>
                    <Field label="WhatsApp (apenas dígitos, com indicativo)">
                        <TxtInput
                            value={data.whatsapp_number || ""}
                            onChange={u("whatsapp_number")}
                            placeholder="351912345678"
                            data-testid="footer-whatsapp"
                        />
                    </Field>
                </div>
            </Card>

            <Card title="Bloco de contacto">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Email *">
                        <TxtInput
                            value={data.email || ""}
                            onChange={u("email")}
                            placeholder="hello@saborliquido.pt"
                            data-testid="footer-email-input"
                        />
                    </Field>
                    <Field label="Telefone (texto exibido)">
                        <TxtInput
                            value={data.phone_display || ""}
                            onChange={u("phone_display")}
                            placeholder="+351 912 345 678"
                            data-testid="footer-phone-display"
                        />
                    </Field>
                    <Field label="Morada / localização">
                        <TxtInput
                            value={data.address_display || ""}
                            onChange={u("address_display")}
                            placeholder="Portugal · Mundo"
                            data-testid="footer-address-display"
                        />
                    </Field>
                </div>
            </Card>

            <Card
                title="Links de navegação"
                action={
                    <button
                        type="button"
                        onClick={addNav}
                        disabled={navLinks.length >= 8}
                        data-testid="footer-add-nav"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-green text-white text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-brand-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="h-3.5 w-3.5" /> Adicionar
                    </button>
                }
            >
                <div className="space-y-3">
                    {navLinks.map((l, i) => (
                        <div
                            key={i}
                            data-testid={`footer-nav-row-${i}`}
                            className="grid grid-cols-[1fr_1.5fr_auto] gap-3 items-end"
                        >
                            <Field label={`Texto #${i + 1}`}>
                                <TxtInput
                                    value={l.label || ""}
                                    onChange={(e) => updateNav(i, "label", e.target.value)}
                                    data-testid={`footer-nav-label-${i}`}
                                />
                            </Field>
                            <Field label="Destino (URL ou âncora)">
                                <TxtInput
                                    value={l.href || ""}
                                    onChange={(e) => updateNav(i, "href", e.target.value)}
                                    placeholder="#produtos ou /linhas/fruit"
                                    data-testid={`footer-nav-href-${i}`}
                                />
                            </Field>
                            <button
                                type="button"
                                onClick={() => removeNav(i)}
                                disabled={navLinks.length <= 1}
                                data-testid={`footer-nav-remove-${i}`}
                                className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 mt-5 disabled:opacity-40"
                            >
                                <Trash2 className="h-3.5 w-3.5 mx-auto" />
                            </button>
                        </div>
                    ))}
                </div>
            </Card>

            <div className="flex justify-end">
                <SaveButton onClick={save} busy={busy} testid="footer-save-bottom" />
            </div>
        </div>
    );
}

// ============================================================
// LINE DETAILS EDITOR — full /linhas/:id page content
// ============================================================
const LINE_KEYS = ["fruit", "detox", "protein", "tea"];
const LINE_THEMES = [
    { v: "orange", label: "Laranja (FRUIT)" },
    { v: "green", label: "Verde (DETOX)" },
    { v: "pink", label: "Rosa (PROTEIN)" },
    { v: "yellow", label: "Amarelo (TEA)" },
];

function LineDetailsEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState("fruit");

    useEffect(() => {
        apiAuth("/admin/content/line_details")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar páginas de linha"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const setLine = (id, partial) =>
        setData((d) => ({ ...d, [id]: { ...(d[id] || {}), ...partial } }));

    const save = async () => {
        setBusy(true);
        try {
            await apiAuth("/admin/content/line_details", {
                method: "PUT",
                data: { data },
            });
            toast.success("Páginas de linha atualizadas");
        } catch (err) {
            toast.error(
                err.response?.data?.detail?.message ||
                    err.response?.data?.detail ||
                    "Erro a guardar"
            );
        } finally {
            setBusy(false);
        }
    };

    const line = data[active] || {};

    return (
        <div className="space-y-6" data-testid="line-details-editor">
            <Card
                title="Páginas /linhas/:id"
                action={
                    <SaveButton
                        onClick={save}
                        busy={busy}
                        testid="line-details-save"
                    />
                }
            >
                <p className="text-xs text-brand-ink-soft -mt-1">
                    Edita o conteúdo das 4 páginas de detalhe (FRUIT, DETOX, PROTEIN, TEA). Escolhe a linha abaixo.
                </p>
                <div className="flex gap-2 flex-wrap mt-3">
                    {LINE_KEYS.map((k) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setActive(k)}
                            data-testid={`line-details-tab-${k}`}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                                active === k
                                    ? "bg-brand-green text-white shadow-pop"
                                    : "bg-brand-cream text-brand-ink hover:bg-brand-green-soft"
                            }`}
                        >
                            {(data[k] && data[k].name) || k.toUpperCase()}
                        </button>
                    ))}
                </div>
            </Card>

            <Card title={`Linha ${(line.name || active).toUpperCase()} — Cabeçalho`}>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Nome da linha (badge)">
                        <TxtInput
                            value={line.name || ""}
                            onChange={(e) => setLine(active, { name: e.target.value })}
                            data-testid={`line-${active}-name`}
                        />
                    </Field>
                    <Field label="Tagline curta">
                        <TxtInput
                            value={line.tagline || ""}
                            onChange={(e) => setLine(active, { tagline: e.target.value })}
                            data-testid={`line-${active}-tagline`}
                        />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="Headline (título grande do hero)">
                            <TxtInput
                                value={line.headline || ""}
                                onChange={(e) => setLine(active, { headline: e.target.value })}
                                data-testid={`line-${active}-headline`}
                            />
                        </Field>
                    </div>
                    <div className="md:col-span-2">
                        <Field label="Descrição (parágrafo abaixo do título)">
                            <TxtArea
                                rows={3}
                                value={line.description || ""}
                                onChange={(e) => setLine(active, { description: e.target.value })}
                                data-testid={`line-${active}-description`}
                            />
                        </Field>
                    </div>
                    <div className="md:col-span-2">
                        <ImageInput
                            value={line.hero_image}
                            onChange={(v) => setLine(active, { hero_image: v })}
                            label="Imagem do hero"
                            testid={`line-${active}-hero-image`}
                        />
                    </div>
                    <Field label="Tema de cor">
                        <select
                            value={line.theme || "orange"}
                            onChange={(e) => setLine(active, { theme: e.target.value })}
                            data-testid={`line-${active}-theme`}
                            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                        >
                            {LINE_THEMES.map((t) => (
                                <option key={t.v} value={t.v}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            </Card>

            <Card title="Sabores">
                <StringList
                    items={line.flavors || []}
                    onChange={(v) => setLine(active, { flavors: v })}
                    placeholder="Ex: Morango"
                    testid={`line-${active}-flavors`}
                />
            </Card>

            <Card title="Ingredientes (lista curta)">
                <StringList
                    items={line.ingredients || []}
                    onChange={(v) => setLine(active, { ingredients: v })}
                    placeholder="Ex: Sem açúcar adicionado"
                    testid={`line-${active}-ingredients`}
                />
            </Card>

            <Card title="Ficha nutricional">
                <KeyValueList
                    items={line.nutrition || []}
                    onChange={(v) => setLine(active, { nutrition: v })}
                    leftKey="label"
                    rightKey="value"
                    leftPlaceholder="Etiqueta (Calorias)"
                    rightPlaceholder="Valor (45 kcal / 100 g)"
                    testid={`line-${active}-nutrition`}
                />
            </Card>

            <Card title="Formatos de embalagem">
                <FormatList
                    items={line.formats || []}
                    onChange={(v) => setLine(active, { formats: v })}
                    testid={`line-${active}-formats`}
                />
            </Card>

            <Card title="Casos de uso">
                <KeyValueList
                    items={line.use_cases || []}
                    onChange={(v) => setLine(active, { use_cases: v })}
                    leftKey="title"
                    rightKey="text"
                    leftPlaceholder="Título (Sumo natural)"
                    rightPlaceholder="Descrição curta"
                    rightTextarea
                    testid={`line-${active}-use-cases`}
                />
            </Card>

            <Card title="Para quem (chips no hero)">
                <StringList
                    items={line.best_for || []}
                    onChange={(v) => setLine(active, { best_for: v })}
                    placeholder="Ex: Cafés"
                    testid={`line-${active}-best-for`}
                />
            </Card>

            <div className="flex justify-end">
                <SaveButton
                    onClick={save}
                    busy={busy}
                    testid="line-details-save-bottom"
                />
            </div>
        </div>
    );
}

// Helper: list of {label,value} or {title,text}
function KeyValueList({
    items,
    onChange,
    leftKey,
    rightKey,
    leftPlaceholder,
    rightPlaceholder,
    rightTextarea,
    testid,
}) {
    const update = (idx, key, val) =>
        onChange(
            items.map((it, i) => (i === idx ? { ...it, [key]: val } : it))
        );
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
    const add = () =>
        onChange([...(items || []), { [leftKey]: "", [rightKey]: "" }]);

    return (
        <div className="space-y-3" data-testid={testid}>
            {(items || []).map((it, i) => (
                <div
                    key={i}
                    data-testid={`${testid}-row-${i}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start"
                >
                    <TxtInput
                        value={it[leftKey] || ""}
                        onChange={(e) => update(i, leftKey, e.target.value)}
                        placeholder={leftPlaceholder}
                        data-testid={`${testid}-left-${i}`}
                    />
                    {rightTextarea ? (
                        <TxtArea
                            rows={2}
                            value={it[rightKey] || ""}
                            onChange={(e) => update(i, rightKey, e.target.value)}
                            placeholder={rightPlaceholder}
                            data-testid={`${testid}-right-${i}`}
                        />
                    ) : (
                        <TxtInput
                            value={it[rightKey] || ""}
                            onChange={(e) => update(i, rightKey, e.target.value)}
                            placeholder={rightPlaceholder}
                            data-testid={`${testid}-right-${i}`}
                        />
                    )}
                    <button
                        type="button"
                        onClick={() => remove(i)}
                        className="h-9 w-9 flex-shrink-0 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                        data-testid={`${testid}-remove-${i}`}
                    >
                        <Trash2 className="h-3.5 w-3.5 mx-auto" />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                data-testid={`${testid}-add`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold"
            >
                <Plus className="h-3 w-3" /> Adicionar
            </button>
        </div>
    );
}

// Helper: list of {type, detail, best}
function FormatList({ items, onChange, testid }) {
    const update = (idx, key, val) =>
        onChange(
            items.map((it, i) => (i === idx ? { ...it, [key]: val } : it))
        );
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
    const add = () =>
        onChange([
            ...(items || []),
            { type: "", detail: "", best: "" },
        ]);

    return (
        <div className="space-y-3" data-testid={testid}>
            {(items || []).map((it, i) => (
                <div
                    key={i}
                    data-testid={`${testid}-row-${i}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-start"
                >
                    <TxtInput
                        value={it.type || ""}
                        onChange={(e) => update(i, "type", e.target.value)}
                        placeholder="Tipo (Bolsa 1 kg)"
                        data-testid={`${testid}-type-${i}`}
                    />
                    <TxtInput
                        value={it.detail || ""}
                        onChange={(e) => update(i, "detail", e.target.value)}
                        placeholder="Detalhe (≈ 10 copos)"
                        data-testid={`${testid}-detail-${i}`}
                    />
                    <TxtInput
                        value={it.best || ""}
                        onChange={(e) => update(i, "best", e.target.value)}
                        placeholder="Indicado para (Cafés)"
                        data-testid={`${testid}-best-${i}`}
                    />
                    <button
                        type="button"
                        onClick={() => remove(i)}
                        className="h-9 w-9 flex-shrink-0 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                        data-testid={`${testid}-remove-${i}`}
                    >
                        <Trash2 className="h-3.5 w-3.5 mx-auto" />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                data-testid={`${testid}-add`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold"
            >
                <Plus className="h-3 w-3" /> Adicionar formato
            </button>
        </div>
    );
}


// ============================================================
// TESTIMONIALS EDITOR
// ============================================================
function TestimonialsEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/testimonials")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar testemunhos"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const u = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
    const items = data.items || [];
    const setItem = (idx, k, v) =>
        setData((d) => ({
            ...d,
            items: d.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
        }));
    const addItem = () =>
        setData((d) => ({
            ...d,
            items: [...(d.items || []), { quote: "", author: "", role: "" }],
        }));
    const removeItem = (idx) =>
        setData((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));

    const save = async () => {
        if (!items.length || items.some((it) => !it.quote?.trim() || !it.author?.trim())) {
            toast.error("Cada testemunho precisa de citação e autor (mínimo 1)");
            return;
        }
        setBusy(true);
        try {
            await apiAuth("/admin/content/testimonials", {
                method: "PUT",
                data: { data },
            });
            toast.success("Testemunhos atualizados");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao guardar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="testimonials-editor">
            <Card
                title="Testemunhos da Home"
                action={<SaveButton onClick={save} busy={busy} testid="content-testimonials-save" />}
            >
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Etiqueta (eyebrow)">
                        <TxtInput value={data.eyebrow || ""} onChange={u("eyebrow")} data-testid="testimonials-eyebrow" />
                    </Field>
                    <Field label="Título">
                        <TxtInput value={data.title_pre || ""} onChange={u("title_pre")} data-testid="testimonials-title-pre" />
                    </Field>
                    <Field label="Título — destacado (itálico)">
                        <TxtInput value={data.title_highlight || ""} onChange={u("title_highlight")} data-testid="testimonials-title-highlight" />
                    </Field>
                </div>
                <div className="space-y-4 pt-4 border-t border-brand-green/10">
                    {items.map((t, i) => (
                        <div
                            key={i}
                            className="rounded-2xl bg-brand-cream/60 p-4 space-y-3"
                            data-testid={`testimonial-editor-item-${i}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft">
                                    Testemunho {i + 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeItem(i)}
                                    data-testid={`testimonial-remove-${i}`}
                                    className="h-8 w-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                            <Field label="Citação">
                                <TxtArea
                                    rows={2}
                                    value={t.quote || ""}
                                    onChange={(e) => setItem(i, "quote", e.target.value)}
                                    data-testid={`testimonial-quote-${i}`}
                                />
                            </Field>
                            <div className="grid md:grid-cols-2 gap-3">
                                <Field label="Autor">
                                    <TxtInput
                                        value={t.author || ""}
                                        onChange={(e) => setItem(i, "author", e.target.value)}
                                        data-testid={`testimonial-author-${i}`}
                                    />
                                </Field>
                                <Field label="Cargo / local (ex: Café Central · Braga)">
                                    <TxtInput
                                        value={t.role || ""}
                                        onChange={(e) => setItem(i, "role", e.target.value)}
                                        data-testid={`testimonial-role-${i}`}
                                    />
                                </Field>
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addItem}
                        data-testid="testimonial-add"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold"
                    >
                        <Plus className="h-3 w-3" /> Adicionar testemunho
                    </button>
                </div>
            </Card>
        </div>
    );
}

// ============================================================
// FAQ EDITOR
// ============================================================
function FaqEditor({ apiAuth }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiAuth("/admin/content/faq")
            .then((r) => setData(r.data || {}))
            .catch(() => toast.error("Erro a carregar FAQ"));
    }, [apiAuth]);

    if (!data) return <Loading />;

    const u = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
    const items = data.items || [];
    const setItem = (idx, k, v) =>
        setData((d) => ({
            ...d,
            items: d.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
        }));
    const addItem = () =>
        setData((d) => ({
            ...d,
            items: [...(d.items || []), { question: "", answer: "" }],
        }));
    const removeItem = (idx) =>
        setData((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));

    const save = async () => {
        if (!items.length || items.some((it) => !it.question?.trim() || !it.answer?.trim())) {
            toast.error("Cada pergunta precisa de pergunta e resposta (mínimo 1)");
            return;
        }
        setBusy(true);
        try {
            await apiAuth("/admin/content/faq", { method: "PUT", data: { data } });
            toast.success("FAQ atualizada");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao guardar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="faq-editor">
            <Card
                title="Perguntas Frequentes da Home"
                action={<SaveButton onClick={save} busy={busy} testid="content-faq-save" />}
            >
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Etiqueta (eyebrow)">
                        <TxtInput value={data.eyebrow || ""} onChange={u("eyebrow")} data-testid="faq-eyebrow" />
                    </Field>
                    <Field label="Título">
                        <TxtInput value={data.title_pre || ""} onChange={u("title_pre")} data-testid="faq-title-pre" />
                    </Field>
                    <Field label="Título — destacado (itálico)">
                        <TxtInput value={data.title_highlight || ""} onChange={u("title_highlight")} data-testid="faq-title-highlight" />
                    </Field>
                </div>
                <div className="space-y-4 pt-4 border-t border-brand-green/10">
                    {items.map((f, i) => (
                        <div
                            key={i}
                            className="rounded-2xl bg-brand-cream/60 p-4 space-y-3"
                            data-testid={`faq-editor-item-${i}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft">
                                    Pergunta {i + 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeItem(i)}
                                    data-testid={`faq-remove-${i}`}
                                    className="h-8 w-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mx-auto" />
                                </button>
                            </div>
                            <Field label="Pergunta">
                                <TxtInput
                                    value={f.question || ""}
                                    onChange={(e) => setItem(i, "question", e.target.value)}
                                    data-testid={`faq-question-${i}`}
                                />
                            </Field>
                            <Field label="Resposta">
                                <TxtArea
                                    rows={2}
                                    value={f.answer || ""}
                                    onChange={(e) => setItem(i, "answer", e.target.value)}
                                    data-testid={`faq-answer-${i}`}
                                />
                            </Field>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addItem}
                        data-testid="faq-add"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-xs font-semibold"
                    >
                        <Plus className="h-3 w-3" /> Adicionar pergunta
                    </button>
                </div>
            </Card>
        </div>
    );
}
